import React from "react";

import {
    Dropdown as UIKitDropdown,
    TextField,
    DropdownContent,
    DropdownSelect,
    DropdownSelectItem,
} from "@proda-ai/ui-kit";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import { isFalsy, isTruthy } from "@/utils/guards";

export type DropdownOption<T> = {
    value: T;
    label: string;
};

export type DropdownProps<T> = JSX.IntrinsicAttributes & {
    options: DropdownOption<T>[];
    value: T | null;
    onChange: (value: T) => void;
    placeholder?: string;
    label?: string;
    hideLabel?: boolean;
    disabled?: boolean;
    className?: string;
    openUpwards?: boolean;
    id?: string;
    sx?: BoxProps["sx"];
};

export const testIds = {
    main: "Dropdown",
    input: "Dropdown-input",
};

const Dropdown = <T extends string | number>(props: DropdownProps<T>) => {
    const {
        options,
        value,
        onChange,
        placeholder = "Select an option",
        label = "Select an option",
        hideLabel = false,
        disabled = false,
        className,
        openUpwards = false,
        id = "controlled-dropdown",
        sx,
    } = props;

    const [isDropdownOpen, setIsDropdownOpen] = React.useState<boolean>(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Close dropdown when openUpwards changes
    React.useEffect(() => {
        setIsDropdownOpen(false);
    }, [openUpwards]);

    const selectedOption = options.find(option => option.value === value);

    const handleSelect = React.useCallback(
        (focusedIndex?: number): void => {
            if (focusedIndex === undefined) return;
            const selectedOption = options[focusedIndex];
            if (!selectedOption) return;
            onChange(selectedOption.value);
            setIsDropdownOpen(false);
        },
        [options, onChange],
    );
    return (
        <Box
            data-testid={testIds.main}
            className={className}
            sx={{
                width: "100%",
                "& input": {
                    border: "1px solid var(--proda-white-150) !important",
                },
                "& input:read-only": {
                    backgroundColor: disabled
                        ? "var(--proda-white-130)"
                        : "white",
                    cursor: disabled ? "not-allowed" : "pointer",
                },
                ...sx,
            }}
        >
            <UIKitDropdown
                isShowing={isDropdownOpen}
                onClose={(): void => setIsDropdownOpen(false)}
                isDismissible={true}
                onItemSelected={handleSelect}
            >
                {isFalsy(openUpwards) && (
                    <TextField
                        ref={inputRef}
                        data-testid={testIds.input}
                        id={id}
                        name={id}
                        hideLabel={hideLabel}
                        label={label}
                        size="medium"
                        trailingIcon={disabled ? undefined : "angle-down"}
                        value={selectedOption?.label ?? ""}
                        placeholder={placeholder}
                        onClick={
                            disabled ? undefined : () => setIsDropdownOpen(true)
                        }
                        readOnly={true}
                        disabled={disabled}
                    />
                )}
                <DropdownContent
                    className={openUpwards ? "dropdown-content-up" : undefined}
                >
                    <DropdownSelect
                        currentIndex={options.findIndex(
                            option => option.value === value,
                        )}
                        role="listbox"
                        labelledById={id}
                    >
                        {options.map(option => (
                            <DropdownSelectItem
                                key={String(option.value)}
                                role="option"
                                onClick={(): void => {
                                    onChange(option.value);
                                    setIsDropdownOpen(false);
                                }}
                                label={option.label}
                            />
                        ))}
                    </DropdownSelect>
                </DropdownContent>
                {isTruthy(openUpwards) && (
                    <TextField
                        ref={inputRef}
                        data-testid={testIds.input}
                        id={id}
                        name={id}
                        hideLabel={hideLabel}
                        label={label}
                        size="medium"
                        trailingIcon={disabled ? undefined : "angle-down"}
                        value={selectedOption?.label ?? ""}
                        placeholder={placeholder}
                        onClick={
                            disabled ? undefined : () => setIsDropdownOpen(true)
                        }
                        readOnly={true}
                        disabled={disabled}
                    />
                )}{" "}
            </UIKitDropdown>
        </Box>
    );
};

export default Dropdown;
